import { FC, useEffect, useState } from "react";
import styles from "./contract-aircrafts.module.scss";
import { useParams } from "react-router-dom";
import { ReactComponent as DoneIcon } from "./assets/done.svg";
import { PreloaderBar } from "../../../../ui/preloaderBar/PreloaderBar";
import { Snackbar } from "../../../../ui/snackbar/Snackbar";
import { TableRowActions } from "../../../../ui/tableRowActions/tableRowActions";
import {
  useDeleteContractAircraftMutation,
  useGetAllContractAircraftsQuery,
} from "./contractAircraftApi";
import { Confirm } from "../../../../ui/confirm/confirm";
import { Button } from "../../../../ui/button/Button";
import { IContarctAircraft } from "./IContactAircraft";
import { AddContractAircraft } from "./addContractAircraft/addContractAircraft";
import { min2HHMM } from "../../../../helpers/time";
import { EditContractAircraft } from "./editContractAircraft/editContractAircraft";

type RowProps = IContarctAircraft & {
  handler: (type: "edit" | "delete", id: number) => boolean;
};

const Row: FC<RowProps> = ({
  id,
  transport,
  departamental,
  limit_time,
  cost,
  handler,
}) => {
  return (
    <tr>
      <td>{transport.number}</td>
      <td>{transport.name}</td>
      <td>{Boolean(departamental) && <DoneIcon />}</td>
      <td>{min2HHMM(limit_time)}</td>
      <td>{cost / 100}</td>
      <td>
        <TableRowActions id={id} handler={handler} />
      </td>
    </tr>
  );
};

export const ContractAircrafts: FC = () => {
  const { id } = useParams();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [ID, setID] = useState<number | null>(null);

  const { data, error, isError, isFetching } = useGetAllContractAircraftsQuery(
    Number(id)
  );
  const [
    deleteWork,
    {
      isError: isDeleteError,
      isLoading: isDeleteProcessing,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteContractAircraftMutation();

  const create = () => {
    setIsAddOpen(true);
  };

  const rowHandler = (type: "edit" | "delete", id: number) => {
    if (isConfirmOpen) return false;
    setIsUXOpen(true);
    setID(id);
    switch (type) {
      case "delete":
        setIsConfirmOpen(true);
        break;

      case "edit":
        setIsEditOpen(true);
        break;

      default:
        break;
    }
    return true;
  };

  useEffect(() => {
    if (!isDeleteSuccess) return;

    setIsConfirmOpen(false);
    setID(null);
  }, [isDeleteSuccess]);

  return (
    <section className={styles.main}>
      <header className={styles.header}>
        <h2>Воздушные суда</h2>
        <div className={styles.actions}>
          <Button onClick={create}>Добавить</Button>
        </div>
      </header>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Бортовой номер</th>
            <th>Тип ВС</th>
            <th>Ведомственное</th>
            <th>Лимит летного времени, час:мин</th>
            <th>Стоимость 1 часа, руб.</th>
            <th></th>
          </tr>
          {data &&
            data.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                contract={row.contract}
                departamental={row.departamental}
                transport={row.transport}
                limit_time={row.limit_time}
                cost={Math.floor(row.cost * 100) / 100}
                handler={rowHandler}
              />
            ))}
        </thead>
      </table>
      {isFetching && <PreloaderBar />}
      {isAddOpen && id && (
        <AddContractAircraft
          close={() => setIsAddOpen(false)}
          contract_id={Number(id)}
        />
      )}
      {isEditOpen && ID && (
        <EditContractAircraft
          close={() => setIsEditOpen(false)}
          id={ID}
          contract_id={Number(id)}
        />
      )}
      {isError && 'status' in error && error.status !== 404 && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка загрузки данных. Попробуйте позже
        </Snackbar>
      )}
      {isConfirmOpen && ID && (
        <Confirm
          confirm={() => deleteWork(ID)}
          reject={() => {
            setIsConfirmOpen(false);
            setID(null);
          }}
          text="Вы действительно хотите удалить воздушное судно из договора?"
          isProcessing={isDeleteProcessing}
        />
      )}
      {isDeleteSuccess && isUXOpen && (
        <Snackbar
          className="success"
          isOpen={isDeleteSuccess}
          onClose={() => setIsUXOpen(false)}
        >
          Воздушное судно удалено
        </Snackbar>
      )}
      {isDeleteError && isUXOpen && (
        <Snackbar
          className="error"
          isOpen={isDeleteError}
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка удаления. Попробуйте позже
        </Snackbar>
      )}
    </section>
  );
};
